import { useTdsCommon } from '../../hooks/useTdsCommon'
import { getStateCode, formatDate, formatDecimalAmount, padString, padNumber, getTdsFormRoute } from '../../utils/tdsCommon'
import { extractProfileDataApi, downloadCsiFileApi } from '../../utils/tdsFileHandlers'
import TdsProfileSection from '../../components/dashboard/TdsProfileSection'
import { getLegacyStorageKey } from '../../utils/storage';
import React, { useState, useEffect, useCallback, useRef } from 'react'

import { motion } from 'framer-motion'
import { FileText, User, Upload, ArrowLeft, Building2, X, Trash2, Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import axios from 'axios'

import TdsDetailsTable from '../../components/dashboard/TdsDetailsTable'
import ChallanDetailsTable from '../../components/dashboard/ChallanDetailsTable'
import TdsSidebar from '../../components/dashboard/TdsSidebar'
import TdsGeneratedFiles from './TdsGeneratedFiles';
import { useError } from '../../contexts/ErrorContext'
import { form144Sections, remittanceOptions144, formatRemittanceCode144 } from '../../utils/tds144Constants'


const stateOptions = [
    'ANDAMAN AND NICOBAR ISLANDS',
    'ANDHRA PRADESH',
    'ARUNACHAL PRADESH',
    'ASSAM',
    'BIHAR',
    'CHANDIGARH',
    'CHHATTISGARH',
    'DADRA & NAGAR HAVELI AND DAMAN & DIU',
    'DELHI',
    'GOA',
    'GUJARAT',
    'HARYANA',
    'HIMACHAL PRADESH',
    'JAMMU & KASHMIR',
    'JHARKHAND',
    'KARNATAKA',
    'KERALA',
    'LADAKH',
    'LAKSHWADEEP',
    'MADHYA PRADESH',
    'MAHARASHTRA',
    'MANIPUR',
    'MEGHALAYA',
    'MIZORAM',
    'NAGALAND',
    'ODISHA',
    'PONDICHERRY',
    'PUNJAB',
    'RAJASTHAN',
    'SIKKIM',
    'TAMIL NADU',
    'TELANGANA',
    'TRIPURA',
    'UTTAR PRADESH',
    'UTTARAKHAND',
    'WEST BENGAL'
];

// SECTION CODES FOR 27Q
const sectionOptions27Q_old = [
    { value: '194E', label: '194E - Payment to Non-Res. Sportsmen/Sports Assn.' }, // 94E
    { value: '195', label: '195 - Other sums payable to Non-Resident' }, // 195
    { value: '196A', label: '196A - Income from units (Non-Resident)' }, // 96A
    { value: '196B', label: '196B - Income from units (Offshore Fund)' }, // 96B
    { value: '196C', label: '196C - Income from foreign currency bonds' }, // 96C
    { value: '196D', label: '196D - Income of FII from securities' }, // 96D - Remittance code 16/27 applicable
    // 194LB, 194LC, 194LD, 192A, 194LBA, 194LBB, 194LBC, 194N
    { value: '194LB', label: '194LB - Income of Infrastructure Debt Fund' }, // 4LB
    { value: '194LC', label: '194LC - Income from Indian Company (Interest)' }, // 4LC
    { value: '194LC(2)(i)', label: '194LC(2)(i) - Income under clause (i)' }, // LC1
    { value: '194LC(2)(ib)', label: '194LC(2)(ib) - Income under clause (ib)' }, // LC2
    { value: '194LC(2)(ic)', label: '194LC(2)(ic) - Income under clause (ic)' }, // LC3
    { value: '194LD', label: '194LD - Interest on certain bonds/Govt. securities' }, // 4LD
    { value: '192A', label: '192A - Payment of accumulated balance to employee (RPF)' }, // 2AA
    { value: '194LBA', label: '194LBA - Income from units of Business Trust' }, // 4BA
    { value: '194LBB', label: '194LBB - Income in respect of units of Investment Fund' }, // LBB
    { value: '194LBC', label: '194LBC - Income in respect of investment in Securitization Trust' }, // LBC
    { value: '194N', label: '194N - Cash Withdrawal > 1 Crore' }, // 94N
    { value: '194LBA(a)', label: '194LBA(a) - Interest from Business Trust (Non-Res)' }, // BA1
    { value: '194LBA(b)', label: '194LBA(b) - Dividend from Business Trust (Non-Res)' }, // BA2
    { value: '194LBA(c)', label: '194LBA(c) - Rent from Business Trust (Non-Res)' }, // BA3
    { value: '194NF', label: '194NF - Cash payment to non-filers' }, // 4NF
    { value: '196D(1A)', label: '196D(1A) - Income of specified fund from securities' }, // 6DA
    { value: '194B', label: '194B - Winnings from lottery/crossword' }, // 94B
    { value: '194B-P', label: '194B-P - Winnings from lottery/crossword (Kind)' }, // 4BP
    { value: '194BA', label: '194BA - Winnings from online games' }, // 9BA
    { value: '194BA-P', label: '194BA-P - Net Winnings from online games (Kind)' }, // 4AP
    { value: '194BB', label: '194BB - Winnings from horse race' }, // 4BB
    { value: '194NC', label: '194NC - Cash payment to Co-op Societies' }, // 4NC
    { value: '194N-FT', label: '194N-FT - Cash payment to Non-filer Co-op Societies' }, // 9FT
    { value: '194T', label: '194T - Payment to partner of firm (Salary/Remuneration)' } // 94T
];

const sectionOptions27Q_new = form144Sections;

// NATURE OF REMITTANCE OPTIONS FOR FORM 144
const remittanceOptions = remittanceOptions144;

const getFvuSectionCode27Q = (uiCode) => {
    const sectionMapping27Q = {
        '194E': '94E',
        '195': '195',
        '196A': '96A',
        '196B': '96B',
        '196C': '96C',
        '196D': '96D',
        '194LB': '4LB',
        '194LC': '4LC',
        '194LC(2)(i)': 'LC1',
        '194LC(2)(ib)': 'LC2',
        '194LC(2)(ic)': 'LC3',
        '194LD': '4LD',
        '192A': '2AA',
        '194LBA': '4BA',
        '194LBB': 'LBB',
        '194LBC': 'LBC',
        '194N': '94N',
        '194LBA(a)': 'BA1',
        '194LBA(b)': 'BA2',
        '194LBA(c)': 'BA3',
        '194NF': '4NF',
        '196D(1A)': '6DA',
        '194B': '94B',
        '194B-P': '4BP',
        '194BA': '9BA',
        '194BA-P': '4AP',
        '194BB': '4BB',
        '194NC': '4NC',
        '194N-FT': '9FT',
        '194T': '94T'
    };
    return sectionMapping27Q[uiCode] || uiCode;
};

// DEDUCTEE CODES FOR 27Q (Same as 27EQ)
const deducteeCodes27Q = [
    { value: '01', label: '01 - Company, other than domestic company' },
    { value: '02', label: '02 - Individual' },
    { value: '03', label: '03 - Hindu Undivided Family' },
    { value: '04', label: '04 - Association of Persons (AOP)' },
    { value: '05', label: '05 - Association of Persons (AOP) (Companies)' },
    { value: '06', label: '06 - Co-operative Society' },
    { value: '07', label: '07 - Firm' },
    { value: '08', label: '08 - Body of individuals' },
    { value: '09', label: '09 - Artificial juridical person' },
    { value: '10', label: '10 - Others' }
]

const formatMinorHead27Q = (val) => {
    if (!val) return ''
    const s = String(val).trim()
    if (s === '100' || s === '200' || s === '400') return s
    const lower = s.toLowerCase()
    if (lower.includes('advance')) return '100';
    if (lower.includes('payable') || lower.includes('taxpayer')) return '200'
    if (lower.includes('regular') || lower.includes('assessment')) return '400'
    const match = s.match(/(100|200|400)/)
    if (match) return match[0]
    return s
}

// APPLICABLE TDS OPTIONS
const applicableTdsOptions = [
    { value: 'A', label: 'A - TDS rate as per Income Tax Act' },
    { value: 'B', label: 'B - TDS rate as per DTAA' }
]


// COUNTRY CODES (Filtered selection for UI, logic supports map)
const uniqueCountries = [
    { name: 'AFGHANISTAN', code: '01' },
    { name: 'AUSTRALIA', code: '15' },
    { name: 'BANGLADESH', code: '22' },
    { name: 'CANADA', code: '42' },
    { name: 'CHINA', code: '48' },
    { name: 'FRANCE', code: '85' },
    { name: 'GERMANY', code: '91' },
    { name: 'HONG KONG', code: '109' },
    { name: 'INDIA', code: '113' },
    { name: 'INDONESIA', code: '114' },
    { name: 'JAPAN', code: '123' },
    { name: 'MALAYSIA', code: '150' },
    { name: 'MAURITIUS', code: '158' },
    { name: 'NEPAL', code: '174' },
    { name: 'NETHERLANDS', code: '175' },
    { name: 'NEW ZEALAND', code: '178' },
    { name: 'PHILIPPINES', code: '195' },
    { name: 'QATAR', code: '201' },
    { name: 'RUSSIA', code: '204' },
    { name: 'SAUDI ARABIA', code: '216' },
    { name: 'SINGAPORE', code: '221' },
    { name: 'SOUTH AFRICA', code: '226' },
    { name: 'SRI LANKA', code: '230' },
    { name: 'SWITZERLAND', code: '237' },
    { name: 'THAILAND', code: '250' },
    { name: 'UNITED ARAB EMIRATES', code: '264' },
    { name: 'UNITED KINGDOM', code: '265' },
    { name: 'UNITED STATES OF AMERICA', code: '267' },
    { name: 'OTHERS', code: '' } // Placeholder for complete list if needed
]
// Note: In a real app with 200+ countries, load from a JSON or separate file. 
// For this snippet, I am using the most common ones or parsing the user request fully if required. 
// Given the prompt lists ALL of them, I should probably include them all or a mechanism.
// I'll stick to a subset or generic input if list is too huge for this file, but to be safe I'll assume usage of a complete list file or just map a few important ones + dynamic.
// Actually, I can paste the full list if I compress it, but it might be large.
// I will create a separate constant file for countries locally or just embed the full list.
// For now, let's use the provided list in a condensed format.

const countryOptions = [
    { value: '01', label: 'AFGHANISTAN' }, { value: '02', label: 'AKROTIRI' }, { value: '03', label: 'ALBANIA' },
    { value: '04', label: 'ALGERIA' }, { value: '05', label: 'AMERICAN SAMOA' }, { value: '06', label: 'ANDORRA' },
    { value: '07', label: 'ANGOLA' }, { value: '08', label: 'ANGUILLA' }, { value: '09', label: 'ANTARCTICA' },
    { value: '10', label: 'ANTIGUA AND BARBUDA' }, { value: '11', label: 'ARGENTINA' }, { value: '12', label: 'ARMENIA' },
    { value: '13', label: 'ARUBA' }, { value: '14', label: 'ASHMORE AND CARTIER ISLANDS' }, { value: '15', label: 'AUSTRALIA' },
    { value: '16', label: 'AUSTRIA' }, { value: '17', label: 'AZERBAIJAN' }, { value: '18', label: 'BAHRAIN' },
    { value: '19', label: 'BAILIWICK OF GUERNSEY' }, { value: '20', label: 'BAILIWICK OF JERSEY' },
    { value: '21', label: 'BAKER ISLAND' }, { value: '22', label: 'BANGLADESH' }, { value: '23', label: 'BARBADOS' },
    { value: '24', label: 'BELARUS' }, { value: '25', label: 'BELGIUM' }, { value: '26', label: 'BELIZE' },
    { value: '27', label: 'BENIN PORTO' }, { value: '28', label: 'BERMUDA' }, { value: '29', label: 'BHUTAN' },
    { value: '30', label: 'BOLIVIA' }, { value: '31', label: 'BOSNIAAND HERZEGOVINA' }, { value: '32', label: 'BOTSWANA' },
    { value: '33', label: 'BOUVET ISLAND' }, { value: '34', label: 'BRAZIL' }, { value: '35', label: 'BRITISH INDIAN OCEAN TERRITORY' },
    { value: '36', label: 'BRUNEI' }, { value: '37', label: 'BULGARIA' }, { value: '38', label: 'BURKINA FASO' },
    { value: '39', label: 'BURUNDI' }, { value: '40', label: 'CAMBODIA' }, { value: '41', label: 'CAMEROON' },
    { value: '42', label: 'CANADA' }, { value: '43', label: 'CAPE VERDE' }, { value: '44', label: 'CAYMAN ISLANDS' },
    { value: '45', label: 'CENTRAL AFRICAN REPUBLIC' }, { value: '46', label: 'CHAD' }, { value: '47', label: 'CHILE' },
    { value: '48', label: 'CHINA' }, { value: '49', label: 'CHRISTMAS ISLAND' }, { value: '50', label: 'CLIPPERTON ISLAND' },
    { value: '51', label: 'COCOS (KEELING) ISLANDS' }, { value: '52', label: 'COLOMBIA' }, { value: '53', label: 'COMMONWEALTH OF PUERTO RICO' },
    { value: '54', label: 'COMMONWEALTH OF THE NORTHERN MARIANA ISLANDS' }, { value: '55', label: 'COMOROS' },
    { value: '56', label: 'CONGO,DEMOCRATIC REPUBLIC OF THE' }, { value: '57', label: 'CONGO,REPUBLIC OF THE' },
    { value: '58', label: 'COOK ISLANDS' }, { value: '59', label: 'CORAL SEA ISLANDS' }, { value: '60', label: 'CORAL SEA ISLANDS TERRITORY' },
    { value: '61', label: 'COSTA RICA' }, { value: '62', label: 'COTE D\'IVOIRE' }, { value: '63', label: 'CROATIA' },
    { value: '64', label: 'CUBA' }, { value: '65', label: 'CYPRUS' }, { value: '66', label: 'CZECH REPUBLIC' },
    { value: '67', label: 'DENMARK' }, { value: '68', label: 'DEPARTMENTAL COLLECTIVITY OF MAYOTTE' }, { value: '69', label: 'DHEKELIA' },
    { value: '70', label: 'DJIBOUTI' }, { value: '71', label: 'DOMINICA' }, { value: '72', label: 'DOMINICAN REPUBLIC' },
    { value: '73', label: 'EAST TIMOR (TIMORLASTE)' }, { value: '74', label: 'ECUADOR' }, { value: '75', label: 'EGYPT' },
    { value: '76', label: 'ELSALVADOR' }, { value: '77', label: 'EQUATORIAL GUINEA' }, { value: '78', label: 'ERITREA' },
    { value: '79', label: 'ESTONIA' }, { value: '80', label: 'ETHIOPIA' }, { value: '81', label: 'FALKLAND ISLANDS (ISLAS MALVINAS)' },
    { value: '82', label: 'FAROE ISLANDS' }, { value: '83', label: 'FIJI' }, { value: '84', label: 'FINLAND' },
    { value: '85', label: 'FRANCE' }, { value: '86', label: 'FRENCH GUIANA' }, { value: '87', label: 'FRENCH POLYNESIA' },
    { value: '88', label: 'FRENCH SOUTHERN ISLANDS' }, { value: '89', label: 'GABON' }, { value: '90', label: 'GEORGIA' },
    { value: '91', label: 'GERMANY' }, { value: '92', label: 'GEURNSEY' }, { value: '93', label: 'GHANA' },
    { value: '94', label: 'GIBRALTAR' }, { value: '95', label: 'GREECE' }, { value: '96', label: 'GREENLAND' },
    { value: '97', label: 'GRENADA' }, { value: '98', label: 'GUADELOUPE' }, { value: '100', label: 'GUAM' },
    { value: '101', label: 'GUATEMALA' }, { value: '102', label: 'GUERNSEY' }, { value: '103', label: 'GUINEA' },
    { value: '104', label: 'GUINEABISSAU' }, { value: '105', label: 'GUYANA' }, { value: '106', label: 'HAITI' },
    { value: '107', label: 'HEARD ISLAND AND MCDONALD ISLANDS' }, { value: '108', label: 'HONDURAS' }, { value: '109', label: 'HONG KONG' },
    { value: '110', label: 'HOWLAND ISLAND' }, { value: '111', label: 'HUNGARY' }, { value: '112', label: 'ICELAND' },
    { value: '113', label: 'INDIA' }, { value: '114', label: 'INDONESIA' }, { value: '115', label: 'IRAN' },
    { value: '116', label: 'IRAQ' }, { value: '117', label: 'IRELAND' }, { value: '118', label: 'ISLE OF MAN' },
    { value: '119', label: 'ISRAEL' }, { value: '120', label: 'ITALY' }, { value: '121', label: 'JAMAICA' },
    { value: '122', label: 'JAN MAYEN' }, { value: '123', label: 'JAPAN' }, { value: '124', label: 'JARVIS ISLAND' },
    { value: '125', label: 'JERSEY' }, { value: '126', label: 'JOHNSTON ATOLL' }, { value: '127', label: 'JORDAN' },
    { value: '128', label: 'KAZAKHSTAN' }, { value: '129', label: 'KENYA' }, { value: '130', label: 'KINGMAN REEF' },
    { value: '131', label: 'KIRIBATI' }, { value: '132', label: 'KOREA, NORTH' }, { value: '133', label: 'KOREA, SOUTH' },
    { value: '134', label: 'KOSOVO' }, { value: '135', label: 'KUWAIT' }, { value: '136', label: 'KYRGYZSTAN' },
    { value: '137', label: 'LAOS' }, { value: '138', label: 'LATVIA' }, { value: '139', label: 'LEBANON' },
    { value: '140', label: 'LESOTHO' }, { value: '141', label: 'LIBERIA' }, { value: '142', label: 'LIBYA' },
    { value: '143', label: 'LIECHTENSTEIN' }, { value: '144', label: 'LITHUANIA' }, { value: '145', label: 'LUXEMBOURG' },
    { value: '146', label: 'MACAU' }, { value: '147', label: 'MACEDONIA' }, { value: '148', label: 'MADAGASCAR' },
    { value: '149', label: 'MALAWI' }, { value: '150', label: 'MALAYSIA' }, { value: '151', label: 'MALAYSIA (LABUAN)' },
    { value: '152', label: 'MALDIVES' }, { value: '153', label: 'MALI' }, { value: '154', label: 'MALTA' },
    { value: '155', label: 'MARSHALLISLANDS' }, { value: '156', label: 'MARTINIQUE' }, { value: '157', label: 'MAURITANIA' },
    { value: '158', label: 'MAURITIUS' }, { value: '159', label: 'MAYOTTE' }, { value: '160', label: 'UNITED MEXICAN STATES' },
    { value: '161', label: 'MICRONESIA, FEDERATED STATES OF' }, { value: '162', label: 'MIDWAY ISLANDS' },
    { value: '163', label: 'MOLDOVA' }, { value: '164', label: 'MONACO' }, { value: '165', label: 'MONGOLIA' },
    { value: '166', label: 'MONTENEGRO' }, { value: '167', label: 'MONTSERRAT' }, { value: '168', label: 'MOROCCO' },
    { value: '169', label: 'MOZAMBIQUE' }, { value: '170', label: 'MYANMAR (BURMA)' }, { value: '171', label: 'NAMIBIA' },
    { value: '172', label: 'NAURU' }, { value: '173', label: 'NAVASSA ISLAND' }, { value: '174', label: 'NEPAL' },
    { value: '175', label: 'NETHERLANDS' }, { value: '176', label: 'NETHERLANDS ANTILLES' }, { value: '177', label: 'NEW CALEDONIA' },
    { value: '178', label: 'NEWZEALAND' }, { value: '179', label: 'NICARAGUA' }, { value: '180', label: 'NIGER' },
    { value: '181', label: 'NIGERIA' }, { value: '182', label: 'NIUE' }, { value: '183', label: 'NORFOLK ISLAND' },
    { value: '184', label: 'NORTHERN MARIANA ISLANDS' }, { value: '185', label: 'NORWAY' }, { value: '186', label: 'OMAN' },
    { value: '187', label: 'PAKISTAN' }, { value: '188', label: 'PALAU' }, { value: '189', label: 'PALMYRA ATOLL' },
    { value: '190', label: 'PANAMA' }, { value: '191', label: 'PAPUA NEW GUINEA' }, { value: '192', label: 'PARACEL ISLANDS' },
    { value: '193', label: 'PARAGUAY' }, { value: '194', label: 'PERU' }, { value: '195', label: 'PHILIPPINES' },
    { value: '196', label: 'PITCAIRN ISLANDS' }, { value: '197', label: 'PITCAIRN, HENDERSON, DUCIE, AND OENO ISLANDS' },
    { value: '198', label: 'POLAND' }, { value: '199', label: 'PORTUGAL' }, { value: '200', label: 'PUERTO RICO' },
    { value: '201', label: 'QATAR' }, { value: '202', label: 'REUNION' }, { value: '203', label: 'ROMANIA' },
    { value: '204', label: 'RUSSIA' }, { value: '205', label: 'RWANDA' }, { value: '206', label: 'SAINT BARTHELEMY' },
    { value: '207', label: 'SAINT HELENA' }, { value: '208', label: 'SAINT KITTS AND NEVIS' }, { value: '209', label: 'SAINT LUCIA' },
    { value: '210', label: 'SAINT MARTIN' }, { value: '211', label: 'SAINT PIERRE AND MIQUELON' },
    { value: '212', label: 'SAINT VINCENT AND THE GRENADINES' }, { value: '213', label: 'SAMOA' }, { value: '214', label: 'SANMARINO' },
    { value: '215', label: 'SAO TOME AND PRINCIPE' }, { value: '216', label: 'SAUDI ARABIA' }, { value: '217', label: 'SENEGAL' },
    { value: '218', label: 'SERBIA' }, { value: '219', label: 'SEYCHELLES' }, { value: '220', label: 'SIERRA LEONE' },
    { value: '221', label: 'SINGAPORE' }, { value: '222', label: 'SLOVAKIA' }, { value: '223', label: 'SLOVENIA' },
    { value: '224', label: 'SOLOMON ISLANDS' }, { value: '225', label: 'SOMALIA' }, { value: '226', label: 'SOUTH AFRICA' },
    { value: '227', label: 'SOUTH GEORGIA AND SOUTH SANDWICH ISLANDS' }, { value: '228', label: 'SPRATLY ISLANDS' },
    { value: '229', label: 'SPAIN' }, { value: '230', label: 'SRI LANKA' }, { value: '231', label: 'ST. VINCENT & GRENADINES' },
    { value: '232', label: 'SUDAN' }, { value: '233', label: 'SURINAME' }, { value: '234', label: 'SVALBARD' },
    { value: '235', label: 'SWAZILAND' }, { value: '236', label: 'SWEDEN' }, { value: '237', label: 'SWITZERLAND' },
    { value: '238', label: 'SYRIA' }, { value: '239', label: 'TAIWAN' }, { value: '240', label: 'TAJIKISTAN' },
    { value: '241', label: 'TANZANIA' }, { value: '242', label: 'TERRITORIAL COLLECTIVITY OF ST. PIERRE & MIQUELON' },
    { value: '243', label: 'TERRITORY OF AMERICAN SAMOA' }, { value: '244', label: 'TERRITORY OF ASHMORE AND CARTIER ISLANDS' },
    { value: '245', label: 'TERRITORY OF CHRISTMAS ISLAND' }, { value: '246', label: 'TERRITORY OF COCOS (KEELING) ISLANDS' },
    { value: '247', label: 'TERRITORY OF GUAM' }, { value: '248', label: 'TERRITORY OF HEARD ISLAND & MCDONALD ISLANDS' },
    { value: '249', label: 'TERRITORY OF NORFOLK ISLAND' }, { value: '250', label: 'THAILAND' }, { value: '251', label: 'THE BAHAMAS' },
    { value: '252', label: 'THE GAMBIA' }, { value: '253', label: 'TOGO' }, { value: '254', label: 'TOKELAU' },
    { value: '255', label: 'TONGA' }, { value: '256', label: 'TRINIDAD AND TOBAGO' }, { value: '257', label: 'TUNISIA' },
    { value: '258', label: 'TURKEY' }, { value: '259', label: 'TURKMENISTAN' }, { value: '260', label: 'TURKS AND CAICOS ISLANDS' },
    { value: '261', label: 'TUVALU' }, { value: '262', label: 'UGANDA' }, { value: '263', label: 'UKRAINE' },
    { value: '264', label: 'UNITED ARAB EMIRATES' }, { value: '265', label: 'UNITED KINGDOM' }, { value: '266', label: 'UNITED STATES VIRGIN ISLANDS' },
    { value: '267', label: 'UNITED STATES OF AMERICA' }, { value: '268', label: 'URUGUAY' }, { value: '269', label: 'UZBEKISTAN' },
    { value: '270', label: 'VANUATU' }, { value: '271', label: 'VATICAN CITY (HOLYSEE)' }, { value: '272', label: 'VENEZUELA' },
    { value: '273', label: 'VIETNAM' }, { value: '274', label: 'VIRGIN ISLANDS, BRITISH' }, { value: '275', label: 'VIRGIN ISLANDS, U.S.' },
    { value: '276', label: 'WAKE ISLAND' }, { value: '277', label: 'WALLIS AND FUTUNA' }, { value: '278', label: 'WESTERN SAHARA' },
    { value: '279', label: 'YEMEN' }, { value: '280', label: 'ZAMBIA' }, { value: '281', label: 'ZIMBABWE' },
    { value: '282', label: 'COMBODIA' }, { value: '283', label: 'CONGO' }, { value: '284', label: 'IVORY COAST' },
    { value: '285', label: 'WEST INDIES' }, { value: '286', label: 'BRITISH VIRGIN ISLANDS' }
];

const Tds144Form = ({ isMobile, onSidebarStateChange }) => {
    const csiFileInputRef = useRef(null);
    console.log('Rendering Tds144Form component');
    const navigate = useNavigate()
    const { addError } = useError()
    const [activeSection, setActiveSection] = useState('profile')
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [sidebarState, setSidebarState] = useState({
        isOpen: false,
        isExpanded: false,
        keepOpen: false,
        hovered: false
    })

    const {
        profileData,
        challanData,
        tdsData,
        originalTdsData,
        csiFile,
        consoFileContent,
        isValidating,
        showCsiUploadPopup,
        showDownloadPrompt,
        selectedFinancialYear,
        selectedQuarter,
        selectedForm,
        selectedReturnType,
        challanDateRange,
        setProfileData,
        setChallanData,
        setTdsData,
        setOriginalTdsData,
        setCsiFile,
        setConsoFileContent,
        setIsValidating,
        setShowCsiUploadPopup,
        setShowDownloadPrompt,
        setSelectedFinancialYear,
        setSelectedQuarter,
        setSelectedForm,
        setSelectedReturnType,
        handlePrepareReturnFile,
        handleValidateReturn,
        handleSkipDownload,
        handleCancelValidation,
        executeValidation
    } = useTdsCommon({
        formName: 'tds27Q',
        defaultFormType: '144',
        isCorrection: false,
        generateTdsStatementFile,
        validateDataForReturn,
        onValidationError: (errors) => {
            addError('Please fix the following errors before preparing return:')
            errors.forEach(err => addError(`• ${err}`))
            setSidebarOpen(true)
            setActiveSection('errors')
        }
    });

    const [isProfileEditing, setIsProfileEditing] = useState(true)
    const [downloadPassword, setDownloadPassword] = useState('')
    const [showDownloadPassword, setShowDownloadPassword] = useState(false)
    const [isDownloadingCsi, setIsDownloadingCsi] = useState(false)

    // Profile Pre-fill Automation State
    const [showPrefillPrompt, setShowPrefillPrompt] = useState(false);
    const [prefillPassword, setPrefillPassword] = useState('');
    const [showPrefillPassword, setShowPrefillPassword] = useState(false);
    const [isExtractingProfile, setIsExtractingProfile] = useState(false);

    const handleChallanDataChange = setChallanData;
    const handleTdsDataChange = setTdsData;


    const handleSectionChange = (section) => {
        setActiveSection(section)
    }

    const handleSidebarClose = () => {
        setSidebarOpen(false)
    }

    const handleSidebarStateChange = useCallback((state) => {
        setSidebarState(state)
    }, [])

    // Sync Section 3 address fields with Section 2 when "Same as above" is checked
    useEffect(() => {
        if (
            profileData?.section3?.sameAsAbove &&
            isProfileEditing &&
            profileData?.section2 &&
            profileData?.section3
        ) {
            const newData = { ...profileData }
            if (!newData.section2) newData.section2 = {}
            if (!newData.section3) newData.section3 = {}

            newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
            newData.section3.flatNo = newData.section2.flatNo || ''
            newData.section3.areaLocality = newData.section2.areaLocality || ''
            newData.section3.roadStreet = newData.section2.roadStreet || ''
            newData.section3.townCity = newData.section2.townCity || ''
            newData.section3.pinCode = newData.section2.pinCode || ''
            newData.section3.state = newData.section2.state || ''
            newData.section3.email = newData.section2.email || ''
            newData.section3.stdCode = newData.section2.stdCode || ''
            newData.section3.telephone = newData.section2.telephoneNo || ''
            setProfileData(newData)
            // Cloud sync via updateProfileData, JSON.stringify(newData))
        }
    }, [
        profileData?.section2?.nameOfPremises,
        profileData?.section2?.flatNo,
        profileData?.section2?.areaLocality,
        profileData?.section2?.roadStreet,
        profileData?.section2?.townCity,
        profileData?.section2?.pinCode,
        profileData?.section2?.state,
        profileData?.section2?.email,
        profileData?.section2?.stdCode,
        profileData?.section2?.telephoneNo,
        profileData?.section3?.sameAsAbove,
        isProfileEditing
    ])

    // Profile form handlers
    const handleProfileInputChange = (field, value) => {
        const keys = field.split('.')
        const newData = JSON.parse(JSON.stringify(profileData || {}))

        if (!newData.section1) newData.section1 = {}
        if (!newData.section2) newData.section2 = {}
        if (!newData.section3) newData.section3 = {}

        let current = newData
        for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
                current[keys[i]] = {}
            }
            current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value

        const isSame = (field === 'section3.sameAsAbove') ? value : newData.section3?.sameAsAbove
        if (isSame) {
            const s2 = newData.section2 || {}
            newData.section3.nameOfPremises = s2.nameOfPremises || ''
            newData.section3.flatNo = s2.flatNo || ''
            newData.section3.areaLocality = s2.areaLocality || ''
            newData.section3.roadStreet = s2.roadStreet || ''
            newData.section3.townCity = s2.townCity || ''
            newData.section3.pinCode = s2.pinCode || ''
            newData.section3.state = s2.state || ''
            newData.section3.email = s2.email || ''
            newData.section3.stdCode = s2.stdCode || ''
            newData.section3.telephone = s2.telephoneNo || ''
        }

        setProfileData(newData)
        // Cloud sync via updateProfileData, JSON.stringify(newData))
    }

    // Validation function for Prepare Return
    function validateDataForReturn() {
        const errors = []
        if (!tdsData || tdsData.length === 0) errors.push('At least one TDS entry is required')
        if (!challanData || challanData.length === 0) errors.push('At least one Challan entry is required')
        if (!profileData?.section1?.taxDeduction) errors.push('TAN of Deductor is required in Profile')
        if (!profileData?.section1?.permanentAccountNumber) errors.push('PAN of Deductor is required in Profile')
        if (!profileData?.section2?.name) errors.push('Name of Deductor is required in Profile')
        if (!profileData?.section2?.state) errors.push('State is required in Profile Section 2')
        if (!profileData?.section3?.name) errors.push('Responsible Person Name is required in Profile')
        if (!profileData?.section1?.typeOfDeduction) errors.push('Deductor Type is required in Profile')
        if (!profileData?.section3?.designation) errors.push('Responsible Person Designation is required in Profile')
        if (!profileData?.section3?.flatNo && !profileData?.section3?.nameOfPremises && !profileData?.section3?.roadStreet) {
            errors.push('Responsible Person Address is required in Profile')
        }
        if (!profileData?.section3?.state) errors.push('Responsible Person State is required in Profile')
        if (!profileData?.section3?.pinCode) errors.push('Responsible Person Pincode is required in Profile')
        if (!profileData?.section3?.email) errors.push('Responsible Person Email is required in Profile')
        if (!profileData?.section3?.mobileNo) errors.push('Responsible Person Mobile is required in Profile')
        if (!profileData?.section3?.permanentAccountNumber) errors.push('Responsible Person PAN is required in Profile')

        if (tdsData && tdsData.length > 0) {
            tdsData.forEach((row, index) => {
                if (!row.panOfDeductee) errors.push(`TDS Entry ${index + 1}: PAN of Deductee is required`)
                if (!row.sectionCode) errors.push(`TDS Entry ${index + 1}: Section Code is required`)
                if (row.tdsAmount === '' || row.tdsAmount === null || row.tdsAmount === undefined) row.tdsAmount = 0;
                if (!row.dateOfPayment) errors.push(`TDS Entry ${index + 1}: Date of Payment/Credit is required`)
                if (!row.dateOfDeduction && (parseFloat(row.tdsAmount || 0) + parseFloat(row.surcharge || 0) + parseFloat(row.cess || 0)) > 0) errors.push(`TDS Entry ${index + 1}: Date of Deduction is required`)
                if (!row.natureOfRemittance) errors.push(`TDS Entry ${index + 1}: Nature of Remittance is required`)
                if (!row.country) errors.push(`TDS Entry ${index + 1}: Country of Residence is required`)
                if (!row.applicableTds) errors.push(`TDS Entry ${index + 1}: Applicable TDS (IT Act/DTAA) is required`)
                if (parseFloat(row.tdsAmount) > 0 && !row.challanId) errors.push(`TDS Entry ${index + 1}: Challan Selection is required`)
            })
        }

        if (challanData && challanData.length > 0) {
            challanData.forEach((row, index) => {
                if (!row.tdsAmount || parseFloat(row.tdsAmount) <= 0) errors.push(`Challan Entry ${index + 1}: TDS Amount is required`)
                if (!row.dateOfDeposit) errors.push(`Challan Entry ${index + 1}: Date of Deposit is required`)
                if (!row.bsrCode) errors.push(`Challan Entry ${index + 1}: BSR Code is required`)
                else if (row.bsrCode.length !== 7) errors.push(`Challan Entry ${index + 1}: BSR Code must be 7 digits`)
                if (!row.challanSerialNo) errors.push(`Challan Entry ${index + 1}: Challan Serial No. is required`)
                else if (row.challanSerialNo.length !== 5) errors.push(`Challan Entry ${index + 1}: Challan Serial No. must be 5 digits`)
                if (!row.minorHead) errors.push(`Challan Entry ${index + 1}: Minor Head of Challan is required`)

                // Advance Tax (100) check for 4BP and 4AP
                const mappedChallanSection = getFvuSectionCode27Q(row.sectionCode)
                if ((mappedChallanSection === '4BP' || mappedChallanSection === '4AP') && row.minorHead !== '100') {
                    errors.push(`Challan Entry ${index + 1}: Minor Head must be '100' (Advance Tax) for sections 194B-P or 194BA-P`)
                }
            })
        }
        return errors
    }

    function generateTdsStatementFile() {
        let fileContent = ''
        let errors = []

        try {
            const section1 = profileData?.section1 || {}
            const section2 = profileData?.section2 || {}
            const section3 = profileData?.section3 || {}

            let financialYear = ''
            let fyStartYear = 0
            if (selectedFinancialYear) {
                const parts = selectedFinancialYear.split('-')
                if (parts.length >= 2) {
                    fyStartYear = parseInt(parts[0])
                    const yearPart2 = parts[1].length === 4 ? parts[1].slice(-2) : parts[1]
                    financialYear = `${parts[0]}${yearPart2}`
                }
            }
            if (!fyStartYear || isNaN(fyStartYear)) {
                const today = new Date()
                fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
                financialYear = `${fyStartYear}${String(fyStartYear + 1).slice(-2)}`
            }
            const ayStart = fyStartYear + 1
            const assessmentYear = `${ayStart}${String(ayStart + 1).slice(-2)}`





            const formatDateString = (dateStr) => {
                if (!dateStr) return ''
                const date = new Date(dateStr)
                if (isNaN(date.getTime())) return ''
                const d = String(date.getDate()).padStart(2, '0')
                const m = String(date.getMonth() + 1).padStart(2, '0')
                const y = date.getFullYear()
                return `${d}${m}${y}`
            }





            // Check deductee validation for PAN and name


            if (challanData && challanData.length > 0) {
                challanData.forEach((row, index) => {
                    if (!row.tdsAmount || parseFloat(row.tdsAmount) <= 0) {
                        errors.push(`Challan Entry ${index + 1}: TDS Amount is required`)
                    }
                    if (!row.dateOfDeposit) {
                        errors.push(`Challan Entry ${index + 1}: Date of Deposit is required`)
                    }
                    if (!row.bookEntry && !row.bsrCode) {
                        errors.push(`Challan Entry ${index + 1}: BSR Code is required`)
                    } else if (!row.bookEntry && row.bsrCode.length !== 7) {
                        errors.push(`Challan Entry ${index + 1}: BSR Code must be 7 digits`)
                    }
                    if (!row.challanSerialNo) {
                        errors.push(`Challan Entry ${index + 1}: Challan Serial No. is required`)
                    } else if (row.challanSerialNo.length !== 5) {
                        errors.push(`Challan Entry ${index + 1}: Challan Serial No. must be 5 digits`)
                    }
                })
            }



            let lineNumber = 1
            const currentDate = new Date()

            // 1. FH (18 fields)
            const fh = []
            fh[1] = lineNumber; // 1. Line Number
            fh[2] = 'FH'; // 2. Record Type
            fh[3] = 'NS1'; // 3. File Type
            fh[4] = 'R'; // 4. Upload Type
            const fileCreationDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '')
            fh[5] = fileCreationDate; // 5. File Creation Date
            fh[6] = '1'; // 6. File Sequence No.
            fh[7] = 'D'; // 7. Uploader Type
            fh[8] = section1.taxDeduction || ''; // 8. TAN of Deductor
            fh[9] = '1'; // 9. Total No. of Batches
            fh[10] = 'Tax Eases Client'; // 10. Name of Return Preparation Utility
            for (let i = 11; i <= 17; i++) fh[i] = ''; // 11-17. Not applicable/Fillers
            fh[18] = ''; // 18. Consolidated file hash
            fileContent += fh.slice(1).join('^') + '\n'; lineNumber++;

            // 2. BH (72 fields)
            const bh = Array(73).fill('')
            const batchTotal = challanData.reduce((sum, c) => sum + parseFloat(c.totalAmount || 0), 0)
            const dAddr1 = `${section2.flatNo || ''} ${section2.nameOfPremises || ''} ${section2.roadStreet || ''}`.trim()
            const rAddr1 = `${section3.flatNo || ''} ${section3.nameOfPremises || ''} ${section3.roadStreet || ''}`.trim()

            bh[1] = lineNumber; // 1. Line Number
            bh[2] = 'BH'; // 2. Record Type
            bh[3] = '1'; // 3. Batch Number
            bh[4] = challanData.length; // 4. Count of Challan
            bh[5] = '144'; // 5. Form Number
            bh[6] = ''; // 6. Transaction Type
            bh[7] = ''; // 7. Batch Updation Indicator
            bh[8] = ''; // 8. Original Token Number
            bh[9] = section1.isEarlierReturnFiled === 'Yes' ? (section1.receiptNoOriginal || '') : ''; // 9. Previous Token
            bh[10] = ''; // 10. Token Number submitted
            bh[11] = ''; // 11. Token Number date
            bh[12] = ''; // 12. Last TAN of Deductor
            bh[13] = section1.taxDeduction || ''; // 13. TAN of Deductor
            bh[14] = ''; // 14. Receipt number provided by TIN
            bh[15] = section1.permanentAccountNumber || 'PANNOTREQD' // 15. PAN of Deductor
            bh[16] = assessmentYear; // 16. Assessment Yr
            bh[17] = financialYear; // 17. Tax Year
            bh[18] = selectedQuarter; // 18. Period
            bh[19] = padString(section2.name || section2.employerName || '', 75); // 19. Name of Deductor
            bh[20] = 'INDIA'; // 20. Deductor Address 6 (Country/Region)
            bh[21] = padString(section2.flatNo || '', 25); // 21. Deductor Address 1
            bh[22] = padString(section2.nameOfPremises || '', 25); // 22. Deductor Address 2
            bh[23] = padString(section2.roadStreet || '', 25); // 23. Deductor Address 3
            bh[24] = padString(section2.areaLocality || '', 25); // 24. Deductor Address 4
            bh[25] = padString(section2.townCity || '', 25); // 25. Deductor Address 5
            bh[26] = getStateCode(section2.state); // 26. Deductor's Address - State
            bh[27] = section2.pinCode || ''; // 27. Deductor's Address - Pincode
            bh[28] = padString(section2.email || '', 75); // 28. Deductor's Email ID
            bh[29] = section2.stdCode || '91'; // 29. Deductor Contact Country Code
            bh[30] = padString(section2.mobileNo || section2.telephoneNo || '', 10); // 30. Deductor Contact Number
            bh[31] = ''; // 31. Filler 1
            bh[32] = section1.typeOfDeduction || section1.deductorType || 'O'; // 32. Deductor Type
            bh[33] = padString(section3.name || section3.nameOfPerson || '', 75); // 33. Name of Person responsible
            bh[34] = padString(section3.designation || '', 20); // 34. Designation
            bh[35] = padString(section3.flatNo || '', 25); // 35. Responsible Person's Address 1
            bh[36] = padString(section3.nameOfPremises || '', 25); // 36. Responsible Person's Address 2
            bh[37] = padString(section3.roadStreet || '', 25); // 37. Responsible Person's Address 3
            bh[38] = padString(section3.areaLocality || '', 25); // 38. Responsible Person's Address 4
            bh[39] = padString(section3.townCity || '', 25); // 39. Responsible Person's Address 5
            bh[40] = getStateCode(section3.state); // 40. Responsible Person's State
            bh[41] = section3.pinCode || ''; // 41. Responsible Person's PIN
            bh[42] = padString(section3.email || '', 75); // 42. Responsible Person's Email ID - 1
            bh[43] = section3.stdCode || '91'; // 43. Responsible Persons country region
            bh[44] = section3.stdCode || '91'; // 44. Responsible Person Contact Country Code
            bh[45] = padString(section3.mobileNo || section3.telephone || '', 10); // 45. Responsible Person Contact Number
            bh[46] = ''; // 46. Filler 3
            bh[47] = formatDecimalAmount(batchTotal); // 47. Batch Total
            bh[48] = ''; // 48. Unmatched challan count
            bh[49] = ''; // 49. Count of Salary Details Records
            bh[50] = ''; // 50. Batch Total of - Gross Total Income
            bh[51] = ''; // 51. Filler 4
            bh[52] = section1.isEarlierReturnFiled === 'Yes' ? 'Y' : 'N'; // 52. Whether regular statement for Form 144 filed
            bh[53] = ''; // 53. Last Deductor Type
            bh[54] = ''; // 54. State Name
            bh[55] = ''; // 55. Filler 5
            bh[56] = ''; // 56. Filler 6
            bh[57] = section1.ministryName || ''; // 57. Ministry Name
            bh[58] = section1.ministryNameOther || ''; // 58. Ministry Name Others
            bh[59] = section3.permanentAccountNumber || section3.pan || ''; // 59. PAN of Responsible Person
            bh[60] = ''; // 60. Filler 7
            bh[61] = ''; // 61. Filler 8
            bh[62] = ''; // 62. Filler 9
            bh[63] = ''; // 63. Filler 10
            bh[64] = ''; // 64. Filler 11
            bh[65] = ''; // 65. Filler 12
            bh[66] = ''; // 66. Filler 13
            bh[67] = ''; // 67. Filler 14
            bh[68] = section1.ain || ''; // 68. AIN
            bh[69] = section2.gstin || section1.gstn || ''; // 69. GSTN
            bh[70] = ''; // 70. Count of Section 194P
            bh[71] = ''; // 71. Batch Total of - Gross Total Income 194P
            bh[72] = ''; // 72. Record Hash

            fileContent += bh.slice(1).join('^') + '\n'; lineNumber++;

            // 3. Challan & Deductee Records
            let actualChallanIndex = 1
            challanData.forEach((challan) => {
                const tdsEntriesForChallan = tdsData.filter(tds => tds.challanId == challan.id)

                const cd = Array(31).fill('')
                cd[1] = lineNumber
                cd[2] = 'CD'
                cd[3] = '1'
                cd[4] = actualChallanIndex
                cd[5] = tdsEntriesForChallan.length
                cd[6] = (parseFloat(challan.totalAmount || 0) === 0) ? 'Y' : 'N'

                cd[8] = parseFloat(challan.tdsAmount || 0).toFixed(2)
                cd[9] = parseFloat(challan.interest || 0).toFixed(2)
                cd[10] = parseFloat(challan.feesAmount || 0).toFixed(2)
                cd[11] = parseFloat(challan.other || challan.penalty || 0).toFixed(2)
                cd[12] = parseFloat(challan.totalAmount || 0).toFixed(2)

                cd[13] = challan.bookEntry ? 'B' : 'C'

                cd[15] = challan.bsrCode || ''
                cd[17] = challan.challanSerialNo || ''
                cd[19] = challan.dateOfDeposit ? formatDateString(challan.dateOfDeposit) : ''

                cd[21] = parseFloat(tdsEntriesForChallan.reduce((sum, t) => sum + parseFloat(t.totalTaxDeposited || t.tdsAmount || 0), 0)).toFixed(2)
                cd[22] = parseFloat(tdsEntriesForChallan.reduce((sum, t) => sum + parseFloat(t.totalTaxDeducted || t.tdsAmount || 0), 0)).toFixed(2)
                cd[23] = challan.minorHead || ''
                cd[24] = parseFloat(challan.interestAllocation !== undefined && challan.interestAllocation !== '' ? challan.interestAllocation : (challan.interest || 0)).toFixed(2)
                cd[25] = parseFloat(challan.otherAllocation !== undefined && challan.otherAllocation !== '' ? challan.otherAllocation : (challan.feesAllocation !== undefined && challan.feesAllocation !== '' ? challan.feesAllocation : (challan.other || challan.penalty || 0))).toFixed(2)

                fileContent += cd.slice(1).join('^') + '\n'
                lineNumber++

                tdsEntriesForChallan.forEach((tds, tdsIndex) => {
                    const dd = Array(46).fill('')
                    dd[1] = lineNumber
                    dd[2] = 'DD'
                    dd[3] = '1'
                    dd[4] = tdsIndex + 1
                    dd[5] = actualChallanIndex
                    dd[6] = 'O'

                    dd[8] = String(tds.panOfDeductee || tds.pan || 'PANNOTAVBL').replace(/[\r\n]/g, '').trim()
                    dd[9] = String(`${tds.firstName || ''} ${tds.middleName || ''} ${tds.lastName || ''}`.trim()).substring(0, 75)

                    let deducteeCodeVal = String(tds.deducteeCode || '1').trim()
                    if (/^\d+$/.test(deducteeCodeVal)) {
                        deducteeCodeVal = String(parseInt(deducteeCodeVal, 10))
                    }
                    dd[12] = deducteeCodeVal
                    const optOut = String(tds.optOutTaxation || 'N').toUpperCase().trim();
                    dd[14] = (optOut === 'Y' || optOut === 'YES') ? 'Y' : 'N';
                    const cleanSecCode = String(tds.sectionCode || '').split('-')[0].trim()
                    dd[15] = cleanSecCode
                    dd[16] = formatRemittanceCode144(tds.natureOfRemittance || tds.remittanceCode, cleanSecCode)
                    dd[17] = tds.country || tds.countryCode || ''

                    dd[18] = 'Y'
                    dd[19] = tds.dateOfPayment ? formatDateString(tds.dateOfPayment) : ''
                    dd[20] = parseFloat(tds.amountOfPayment || 0).toFixed(2)

                    const grossedUpVal = String(tds.grossedUp393_10 || tds.grossedUp || 'N').toUpperCase().trim();
                    dd[21] = (grossedUpVal === 'Y' || grossedUpVal === 'YES') ? 'Y' : 'N';
                    dd[22] = ''
                    dd[23] = ''

                    const totalTax = parseFloat(tds.tdsAmount || 0) + parseFloat(tds.surcharge || 0) + parseFloat(tds.cess || 0)
                    dd[24] = totalTax.toFixed(2)
                    dd[25] = totalTax.toFixed(2)

                    dd[27] = tds.dateOfDeduction ? formatDateString(tds.dateOfDeduction) : ''

                    dd[28] = parseFloat(tds.rateOfTax || 0).toFixed(4)

                    const taxRateAsPer = String(tds.taxRateAsPer || tds.applicableTds || 'A').toUpperCase().trim();
                    dd[30] = (taxRateAsPer === 'B') ? 'B' : 'A';

                    dd[31] = tds.ackNo15CA || ''
                    dd[32] = tds.reasonForNonDeduction || ''
                    dd[33] = tds.certificateNo197 || ''

                    dd[37] = padString(tds.addressInCountryOfResidence || '', 250).trim()
                    dd[38] = tds.emailId || ''
                    dd[39] = tds.contactNumber || ''

                    dd[41] = tds.taxIdentificationNumber || ''

                    fileContent += dd.slice(1).join('^') + '\n'
                    lineNumber++
                })
                actualChallanIndex++
            })

            return fileContent.trim()
        } catch (error) {
            console.error('Error generating statement file:', error)
            throw error;
        }
    }




    const handleCsiFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setCsiFile(e.target.files[0])
            setShowCsiUploadPopup(false)
            executeValidation(e.target.files[0])
        }
    }

    const handleCsiDownload = async () => {
        if (!downloadPassword) {
            toast.error('Please enter Income Tax Portal Password');
            return;
        }

        setIsDownloadingCsi(true);
        try {
            const { from, to } = challanDateRange;
            if (!from || !to) {
                toast.error('Could not determine date range from challans. Please upload CSI manually.');
                setIsDownloadingCsi(false);
                return;
            }

            const response = await axios.post('/api/login-income-tax', {
                credentials: {
                    tan: profileData?.section1?.taxDeduction || profileData?.section1?.tan,
                    password: downloadPassword
                },
                dateRange: { from, to }
            });

            if (response.data.success && response.data.csiContent) {
                toast.success('CSI File Downloaded Successfully!');
                setShowDownloadPrompt(false);
                if (typeof setDownloadPassword === 'function') setDownloadPassword('');

                const binaryString = window.atob(response.data.csiContent.replace(/\s/g, ''));
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const file = new File([bytes], response.data.fileName || 'downloaded.csi', { type: 'text/plain' });
                if (typeof setCsiFile === 'function') setCsiFile(file);
                if (typeof executeValidation === 'function') executeValidation(file);
            } else {
                const errMsg = response.data.error || 'Failed to download CSI file. Please check password.';
                toast.error(errMsg);
            }
        } catch (error) {
            console.error('CSI Download Error:', error);
            toast.error('An error occurred during download');
        } finally {
            setIsDownloadingCsi(false);
        }
    };

    const handleProfilePrefill = async () => {
        if (!prefillPassword) {
            toast.error('Portal password is required');
            return;
        }

        setIsExtractingProfile(true);
        try {
            const { success, data, error } = await extractProfileDataApi({
                tan: profileData.section1?.taxDeduction,
                password: prefillPassword
            });

            if (success && data) {
                const newData = { ...profileData };
                if (data.pan) {
                    newData.section1.permanentAccountNumber = data.pan;
                    if (newData.section3) newData.section3.permanentAccountNumber = data.pan;
                }
                if (data.name) {
                    newData.section2.name = data.name;
                }
                if (data.keyPerson) {
                    if (data.keyPerson.name) newData.section3.name = data.keyPerson.name;
                    if (data.keyPerson.pan) newData.section3.permanentAccountNumber = data.keyPerson.pan;
                }
                if (data.email) {
                    newData.section2.email = data.email;
                    if (newData.section3) newData.section3.email = data.email;
                }
                if (data.mobile && newData.section3) {
                    newData.section3.mobileNo = data.mobile;
                }

                if (data.address) {
                    newData.section2.flatNo = data.address.flatNo || '';
                    newData.section2.nameOfPremises = data.address.premisesName || '';
                    newData.section2.roadStreet = data.address.roadStreet || '';
                    newData.section2.areaLocality = data.address.areaLocality || '';
                    newData.section2.townCity = data.address.city || '';
                    newData.section2.pinCode = data.address.pinCode || '';

                    // Enhanced State Matching
                    if (data.address.state) {
                        const upperState = data.address.state.toUpperCase().trim();
                        // Try explicit match first, then partial
                        const matchedState = stateOptions.find(opt => opt === upperState) ||
                            stateOptions.find(opt => upperState.includes(opt) || opt.includes(upperState));

                        if (matchedState) {
                            newData.section2.state = matchedState;
                        } else {
                            // Fallback: If no match found, don't set invalid value for dropdown
                            console.warn('State mismatch:', data.address.state);
                        }
                    }

                    if (newData.section3 && newData.section3.sameAsAbove) {
                        newData.section3.flatNo = newData.section2.flatNo;
                        newData.section3.nameOfPremises = newData.section2.nameOfPremises;
                        newData.section3.roadStreet = newData.section2.roadStreet;
                        newData.section3.areaLocality = newData.section2.areaLocality;
                        newData.section3.townCity = newData.section2.townCity;
                        newData.section3.pinCode = newData.section2.pinCode;
                        newData.section3.state = newData.section2.state;
                    }
                }

                setProfileData(newData);
                // Cloud sync via updateProfileData, JSON.stringify(newData));
                toast.success('Profile data pre-filled successfully!');
                setShowPrefillPrompt(false);
                setPrefillPassword('');
            } else {
                toast.error(error || 'Extraction failed');
            }
        } catch (error) {
            toast.error('Extraction Failed: ' + (error.message));
        } finally {
            setIsExtractingProfile(false);
        }
    };

    const parseConsoFile = (consoText) => {
        try {
            const lines = consoText.split('\n')

            // Validate BH record form type
            const bhLine = lines.find(l => {
                const p = l.split('^')
                return p[1] === 'BH'
            })

            if (bhLine) {
                const bhParts = bhLine.split('^')
                const formInConso = (bhParts[4] || '').toUpperCase().trim()
                if (formInConso && formInConso !== '144' && formInConso !== '27Q') {
                    toast.error(`Invalid Conso File! Expected Form 144, but file contains Form ${formInConso}.`)
                    alert(`Invalid Conso File!\n\nThis form is Form 144, but the uploaded Conso file belongs to Form ${formInConso}.\nPlease upload the correct Form 144 Conso file.`)
                    return
                }
            }

            const newChallans = []
            const newTdsData = []
            let currentChallan = null
            let rowIndex = 1

            lines.forEach(line => {
                if (!line.trim()) return
                const parts = line.split('^')
                const recordType = parts[1]

                if (recordType === 'BH') {
                    const stateCodeMap = {
                        '01': 'ANDAMAN AND NICOBAR ISLANDS', '02': 'ANDHRA PRADESH', '03': 'ARUNACHAL PRADESH', '04': 'ASSAM', '05': 'BIHAR',
                        '06': 'CHANDIGARH', '07': 'DADRA AND NAGAR HAVELI', '08': 'DAMAN AND DIU', '09': 'DELHI', '10': 'GOA',
                        '11': 'GUJARAT', '12': 'HARYANA', '13': 'HIMACHAL PRADESH', '14': 'JAMMU AND KASHMIR', '15': 'KARNATAKA',
                        '16': 'KERALA', '17': 'LAKSHADWEEP', '18': 'MADHYA PRADESH', '19': 'MAHARASHTRA', '20': 'MANIPUR',
                        '21': 'MEGHALAYA', '22': 'MIZORAM', '23': 'NAGALAND', '24': 'ORISSA', '25': 'PONDICHERRY',
                        '26': 'PUNJAB', '27': 'RAJASTHAN', '28': 'SIKKIM', '29': 'TAMIL NADU', '30': 'TRIPURA',
                        '31': 'UTTAR PRADESH', '32': 'WEST BENGAL', '33': 'CHHATTISGARH', '34': 'UTTARANCHAL', '35': 'JHARKHAND',
                        '36': 'TELANGANA', '37': 'LADAKH', '99': 'FOREIGN'
                    };
                    const getMappedState = (code) => {
                        if (!code) return '';
                        const padded = code.padStart(2, '0');
                        return stateCodeMap[padded] || code || '';
                    };
                    setProfileData(prev => ({
                        ...prev,
                        section1: {
                            ...prev.section1,
                            taxDeduction: parts[12] || '', // TAN (Field 13)
                            permanentAccountNumber: parts[14] || '', // PAN (Field 15)
                            typeOfDeduction: parts[31] || '', // Type/Status (Field 32)
                            receiptNoOriginal: parts[7] || '', // Token No (Field 8)
                            receiptNoEarlier: parts[8] || '' // Prev Token No (Field 9)
                        },
                        section2: {
                            ...prev.section2,
                            name: parts[18] || '', // Field 19
                            branchDivision: parts[19] || '', // Field 20
                            flatNo: parts[20] || '', // Field 21
                            nameOfPremises: parts[21] || '', // Field 22
                            roadStreet: parts[22] || '', // Field 23
                            areaLocality: parts[23] || '', // Field 24
                            townCity: parts[24] || '', // Field 25
                            state: getMappedState(parts[25]), // Field 26
                            pinCode: parts[26] || '', // Field 27
                            email: parts[27] || '', // Field 28
                            stdCode: parts[28] || '', // Field 29
                            telephoneNo: parts[29] || '', // Field 30
                            gstin: parts[68] || '', // Field 69
                        },
                        section3: {
                            ...prev.section3,
                            name: parts[32] || '', // Field 33
                            designation: parts[33] || '', // Field 34
                            flatNo: parts[34] || '', // Field 35
                            nameOfPremises: parts[35] || '', // Field 36
                            roadStreet: parts[36] || '', // Field 37
                            areaLocality: parts[37] || '', // Field 38
                            townCity: parts[38] || '', // Field 39
                            state: getMappedState(parts[39]), // Field 40
                            pinCode: parts[40] || '', // Field 41
                            email: parts[41] || '', // Field 42
                            mobileNo: parts[47] || '', // Field 48 (TRACES Conso)
                            stdCode: parts[43] || '', // Field 44
                            telephone: parts[44] || '', // Field 45 (STD is 44)
                            telephoneAlternate: parts[45] || '', // Field 46
                            permanentAccountNumber: parts[58] || '', // Field 59
                            hasAddressChanged: parts[46] === 'Y' ? 'Yes' : 'No' // Field 47
                        }
                    }))
                } else if (recordType === 'CD') {
                    const challan = {
                        id: Date.now() + rowIndex++,
                        challanSerialNo: parts[11] || '',
                        bsrCode: parts[15] || '',
                        dateOfDeposit: parts[17] ? `${parts[17].slice(4, 8)}-${parts[17].slice(2, 4)}-${parts[17].slice(0, 2)}` : '',
                        minorHead: parts[39] || '200',
                        tdsAmount: parseFloat(parts[21] || 0),
                        surcharge: parseFloat(parts[22] || 0),
                        educationCess: parseFloat(parts[23] || 0),
                        interest: parseFloat(parts[24] || 0),
                        other: parseFloat(parts[25] || 0),
                        totalAmount: parseFloat(parts[26] || 0),
                        feesAmount: parseFloat(parts[38] || 0),
                        interestAllocation: parseFloat(parts[33] !== undefined && parts[33] !== '' ? parts[33] : (parts[24] || 0)),
                        feesAllocation: parseFloat(parts[34] !== undefined && parts[34] !== '' ? parts[34] : (parts[38] || parts[25] || 0)),
                        otherAllocation: parseFloat(parts[34] !== undefined && parts[34] !== '' ? parts[34] : (parts[38] || parts[25] || 0)),
                        bookEntry: parts[36] === 'Y',
                        higherEducationCess: 0
                    }
                    currentChallan = challan
                    newChallans.push(challan)
                } else if (recordType === 'DD') {
                    if (!currentChallan) return
                    const fullName = (parts[12] || '').trim();
                    const deductee = {
                        id: Date.now() + rowIndex++,
                        challanId: currentChallan.id,
                        deducteeCode: parts[7] || '02',
                        panOfDeductee: parts[9] || '',
                        firstName: fullName,
                        middleName: '',
                        lastName: '',
                        amountOfPayment: parseFloat(parts[21] || 0),
                        dateOfPayment: parts[22] ? `${parts[22].slice(4, 8)}-${parts[22].slice(2, 4)}-${parts[22].slice(0, 2)}` : '',
                        dateOfDeduction: parts[23] ? `${parts[23].slice(4, 8)}-${parts[23].slice(2, 4)}-${parts[23].slice(0, 2)}` : '',
                        rateOfTax: (parseFloat(parts[25] || 0) * 100).toFixed(2),
                        tdsAmount: parseFloat(parts[13] || 0),
                        surcharge: parseFloat(parts[14] || 0),
                        cess: parseFloat(parts[15] || 0),
                        sectionCode: parts[33] || '',
                        reasonForNonDeduction: parts[29] || '',
                        deducteeReferenceNo: parts[11] || '',
                        certificateNo197: parts[34] || '',
                        recordNo: parts[4] || ''
                    }
                    newTdsData.push(deductee)
                }
            })

            setChallanData(newChallans)
            setTdsData(newTdsData)
            setOriginalTdsData(JSON.parse(JSON.stringify(newTdsData)))
            toast.success('Conso File Uploaded and Parsed!')
        } catch (err) {
            console.error(err)
            toast.error('Error parsing conso file')
        }
    }

    const handleConsoFileUpload = (e) => {
        const file = e.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            setConsoFileContent(ev.target.result)
            parseConsoFile(ev.target.result)
        }
        reader.readAsText(file)
    }

        ;

    ;





    // Effect for Prepare Return signal


    const today = new Date();


    const currMonth = today.getMonth(); // 0=Jan, 3=Apr


    const currYear = today.getFullYear();


    const currentFyStart = currMonth >= 3 ? currYear : currYear - 1;





    const financialYearOptions = [];


    for (let start = currentFyStart; start >= 2023; start--) {


        financialYearOptions.push(`${start}-${(start + 1).toString().slice(-2)}`);


    }
    const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'];
    const formOptions = ['27Q'];

    const handleClearData = () => {
        if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
            const initialProfile = {
                section1: {
                    taxDeduction: '',
                    permanentAccountNumber: '',
                    typeOfDeduction: '',
                    receiptNoOriginal: '',
                    receiptNoEarlier: '',
                    isEarlierReturnFiled: 'No'
                },
                section2: {
                    name: '',
                    branchDivision: '',
                    flatNo: '',
                    nameOfPremises: '',
                    roadStreet: '',
                    areaLocality: '',
                    townCity: '',
                    pinCode: '',
                    state: '',
                    email: '',
                    stdCode: '',
                    telephoneNo: ''
                },
                section3: {
                    sameAsAbove: false,
                    name: '',
                    designation: '',
                    permanentAccountNumber: '',
                    nameOfPremises: '',
                    flatNo: '',
                    areaLocality: '',
                    roadStreet: '',
                    pinCode: '',
                    townCity: '',
                    telephone: '',
                    telephoneAlternate: '',
                    state: '',
                    email: '',
                    emailAlternate: '',
                    mobileNo: '',
                    hasAddressChanged: '',
                    hasRegularStatement: ''
                }
            }

            setChallanData([])
            setTdsData([])
            setOriginalTdsData([])
            setProfileData(initialProfile)

            localStorage.removeItem(getLegacyStorageKey('144', 'ChallanData'))
            localStorage.removeItem(getLegacyStorageKey('144', 'TdsData'))
            localStorage.removeItem(getLegacyStorageKey('144', 'ProfileData'))
            localStorage.removeItem(getLegacyStorageKey('144', 'OriginalTdsData'))

            toast.success('All data cleared successfully')
        }
    }

    // ... Render ...
    return (
        <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
            <ToastContainer position="top-right" autoClose={3000} />

            <header className={`bg-white shadow-sm border-b transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''}`} style={{
                width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%'
            }}>
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 relative">
                        <div className="flex items-center">
                            <FileText className="w-6 h-6 mr-2 text-blue-600" />
                        </div>
                        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <h1 className="text-xl font-bold text-gray-800">Form 144 (Non-Residents)</h1>
                        </div>
                        <button
                            onClick={handleClearData}
                            className="flex items-center text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Clear Data
                        </button>
                    </div>
                </div>
            </header>

            <main className={`py-4 px-2 sm:px-4 lg:px-6 transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''}`} style={{
                width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                minHeight: 'calc(100vh - 4rem)'
            }}>
                <div className="p-4 md:p-8 pt-6">


                    {/* Selection Controls */}
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
                        <div className="flex flex-wrap gap-4 items-end">
                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Financial Year <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedFinancialYear}
                                    onChange={(e) => {
                                        const newYear = e.target.value;
                                        setSelectedFinancialYear(newYear);
                                        localStorage.setItem('tdsFinancialYear', newYear);

                                        // Auto-update selectedForm if it doesn't match the year's format
                                        if (newYear) {
                                            const isNewFormat = parseInt(newYear.split('-')[0]) >= 2026;
                                            setSelectedForm(prevForm => {
                                                const f = prevForm;
                                                const mapped = isNewFormat
                                                    ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f === '24Q4' ? '24Q4' : f)
                                                    : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f === '24Q4' ? '24Q4' : f);
                                                if (mapped !== prevForm) {
                                                    localStorage.setItem('tdsForm', mapped);
                                                    return mapped;
                                                }
                                                return prevForm;
                                            });
                                        }
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                >
                                    <option value="">Select Financial Year</option>
                                    {financialYearOptions.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Quarter <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedQuarter}
                                    onChange={(e) => {
                                        setSelectedQuarter(e.target.value)
                                        localStorage.setItem('tdsQuarter', e.target.value)
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                >
                                    <option value="">Select Quarter</option>
                                    {quarterOptions.map(quarter => (
                                        <option key={quarter} value={quarter}>{quarter}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Form Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedForm}
                                    onChange={(e) => {
                                        const newForm = e.target.value;
                                        setSelectedForm(newForm);
                                        localStorage.setItem('tdsForm', newForm);
                                        const targetRoute = getTdsFormRoute(newForm, selectedReturnType, selectedFinancialYear);
                                        navigate(targetRoute);
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                >
                                    {['24Q', '26Q', '27Q', '27EQ'].map(formVal => {
                                        const f = formVal;
                                        const actualForm = selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026
                                            ? (f === '24Q' ? '138' : (f === '26Q' || f === '140') ? '140' : f === '27Q' ? '144' : f === '27EQ' ? '143' : f)
                                            : (f === '138' ? '24Q' : (f === '140' || f === '26Q') ? '26Q' : f === '144' ? '27Q' : f === '143' ? '27EQ' : f);
                                        return (
                                            <option key={formVal} value={actualForm}>
                                                {actualForm}
                                            </option>
                                        )
                                    })}
                                </select>
                            </div>

                            <div className="flex-1 min-w-[180px] w-full">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Return Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedReturnType}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedReturnType(val)
                                        localStorage.setItem('SELECTED_RETURN_TYPE', val)
                                        const targetRoute = getTdsFormRoute(selectedForm, val, selectedFinancialYear);
                                        navigate(targetRoute);
                                    }}
                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                                >
                                    <option value="">Select Return Type</option>
                                    <option value="Regular">Regular</option>
                                    <option value="Correction">Correction</option>
                                </select>
                            </div>

                            <div className="flex-shrink-0">
                                <button
                                    onClick={() => {
                                        const targetForm = selectedForm ? selectedForm.toLowerCase() : '27q';
                                        const isCorrection = selectedReturnType === 'Correction';
                                        const suffix = isCorrection ? '-correction' : '';
                                        const targetPath = `/tds-dashboard/${targetForm}${suffix}`;
                                        if (location.pathname !== targetPath) {
                                            navigate(targetPath);
                                            toast.success('Selection Applied');
                                        } else {
                                            toast.success('Selection Applied');
                                        }
                                    }}
                                    className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium shadow-md"
                                >
                                    Apply Selection
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Receipt Details Section (Conditional) */}
                    {(selectedReturnType === 'Correction' || selectedReturnType === 'Regular') && (
                        <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100 overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-2 h-full bg-orange-500"></div>
                            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                                <Building2 className="w-5 h-5 mr-2 text-orange-600" />
                                Statement Receipt Details <span className="text-red-500 ml-1">*</span>
                            </h3>

                            <div className="space-y-6">
                                {/* REVISED RETURN CASE - PREMIUM UI */}
                                {selectedReturnType === 'Correction' && (
                                    <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                                            {/* Conso Upload Area */}
                                            <div className="lg:col-span-1">
                                                <div className="flex flex-col items-center text-center">
                                                    <input
                                                        type="file"
                                                        accept=".tds"
                                                        onChange={handleConsoFileUpload}
                                                        className="hidden"
                                                        id="conso-upload-premium"
                                                    />
                                                    <label
                                                        htmlFor="conso-upload-premium"
                                                        className="group relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-orange-300 rounded-2xl bg-white hover:bg-orange-50 hover:border-orange-500 transition-all cursor-pointer overflow-hidden shadow-sm"
                                                    >
                                                        {consoFileContent ? (
                                                            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex flex-col items-center text-green-600">
                                                                <div className="bg-green-100 p-3 rounded-full mb-2">
                                                                    <FileText className="w-8 h-8" />
                                                                </div>
                                                                <span className="text-sm font-bold">Conso File Ready</span>
                                                                <span className="text-xs text-green-500 mt-1">Click to Replace</span>
                                                            </motion.div>
                                                        ) : (
                                                            <div className="flex flex-col items-center text-gray-500">
                                                                <div className="bg-orange-100 p-3 rounded-full mb-3 group-hover:bg-orange-200 transition-colors">
                                                                    <Upload className="w-8 h-8 text-orange-600" />
                                                                </div>
                                                                <span className="text-sm font-bold text-gray-700 group-hover:text-orange-700">Upload Conso File</span>
                                                                <span className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider font-medium">Click to browse (.tds)</span>
                                                            </div>
                                                        )}

                                                        {/* Animated background glow */}
                                                        <div className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none"></div>
                                                    </label>

                                                    <p className="mt-3 text-xs text-gray-500 text-center flex items-center">
                                                        <User className="w-3 h-3 mr-1" />
                                                        Upload Consolidated .tds file from TRACES
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Auto-filled Fields */}
                                            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                                                        Receipt No. of Original Return
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            value={profileData?.section1?.receiptNoOriginal || ''}
                                                            disabled
                                                            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                                                            placeholder="Waiting for Conso upload..."
                                                        />
                                                        {profileData?.section1?.receiptNoOriginal && (
                                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                                                Verified
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                                                        Receipt No. of Earlier Statement
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            value={profileData?.section1?.receiptNoEarlier || ''}
                                                            disabled
                                                            className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl text-gray-700 font-medium shadow-sm cursor-not-allowed"
                                                            placeholder="Waiting for Conso upload..."
                                                        />
                                                        {profileData?.section1?.receiptNoEarlier && (
                                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                                                                Verified
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="md:col-span-2 flex items-center p-3 bg-white rounded-lg border border-orange-100 shadow-sm">
                                                    <div className="bg-orange-100 p-2 rounded-lg mr-3">
                                                        <ArrowLeft className="w-4 h-4 text-orange-600" />
                                                    </div>
                                                    <p className="text-xs text-gray-600">
                                                        <span className="font-bold text-orange-600">Note:</span> These details are auto-extracted from your Conso file to ensure correctness and prevent validation failures.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ORIGINAL RETURN CASE */}
                                {selectedReturnType === 'Regular' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Is earlier Return Filed?
                                            </label>
                                            <select
                                                value={profileData?.section1?.isEarlierReturnFiled || 'No'}
                                                onChange={(e) => handleProfileInputChange('section1.isEarlierReturnFiled', e.target.value)}
                                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                                            >
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                        </div>
                                        {profileData?.section1?.isEarlierReturnFiled === 'Yes' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Receipt No. of Original Return <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={profileData?.section1?.receiptNoOriginal || ''}
                                                    onChange={(e) => handleProfileInputChange('section1.receiptNoOriginal', e.target.value)}
                                                    required
                                                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 text-gray-900"
                                                    placeholder="Enter 15-digit Receipt Number"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Navigation Tabs */}
                    <div className="flex space-x-4 mb-6 border-b border-gray-200">
                        <button
                            onClick={() => setActiveSection('tds-details')}
                            className={`pb-2 px-4 font-medium transition-colors relative ${activeSection === 'tds-details'
                                ? 'text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            TDS Details
                            {activeSection === 'tds-details' && (
                                <motion.div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" layoutId="activeTab" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveSection('challan-details')}
                            className={`pb-2 px-4 font-medium transition-colors relative ${activeSection === 'challan-details'
                                ? 'text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Challan Details
                            {activeSection === 'challan-details' && (
                                <motion.div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" layoutId="activeTab" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveSection('generated-files')}
                            className={`pb-2 px-4 font-medium transition-colors relative ${activeSection === 'generated-files'
                                ? 'text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Generated Files
                            {activeSection === 'generated-files' && (
                                <motion.div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" layoutId="activeTab" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveSection('profile')}
                            className={`pb-2 px-4 font-medium transition-colors ml-auto flex items-center relative ${activeSection === 'profile'
                                ? 'text-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <User className="w-4 h-4 mr-2" />
                            Profile & Settings
                            {activeSection === 'profile' && (
                                <motion.div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" layoutId="activeTab" />
                            )}
                        </button>
                    </div>

                    {/* Content Area */}
                    <div className="min-h-[500px]">
                        {activeSection === 'profile' && (
                            <TdsProfileSection
                                profileData={profileData}
                                isProfileEditing={isProfileEditing}
                                onProfileDataChange={handleProfileInputChange}
                                onPrefillClick={() => setShowPrefillPrompt(true)}
                                selectedReturnType={selectedReturnType}
                            />
                        )}

                        {activeSection === 'tds-details' && (
                            <TdsDetailsTable
                                tdsData={tdsData}
                                challanData={challanData}
                                onTdsDataChange={handleTdsDataChange}
                                selectedFinancialYear={selectedFinancialYear}
                                selectedQuarter={selectedQuarter}
                                isRevised={selectedReturnType === 'Correction'}
                                originalData={originalTdsData}
                                is27Q={true}
                                formType="144"
                                sectionOptions={form144Sections}
                                remittanceOptions={remittanceOptions}
                                countryOptions={countryOptions}
                                applicableTdsOptions={applicableTdsOptions}
                                collecteeOptions={deducteeCodes27Q}
                            />
                        )}

                        {activeSection === 'challan-details' && (

                            <ChallanDetailsTable
                                challanData={challanData}
                                tdsData={tdsData}
                                onChallanDataChange={handleChallanDataChange}
                                tan={profileData?.section1?.taxDeduction}
                                financialYear={selectedFinancialYear}
                                isRevised={selectedReturnType === 'Correction'}
                                sectionOptions={form144Sections}
                            />
                        )}

                        {activeSection === 'generated-files' && (

                            <TdsGeneratedFiles isEmbedded={true} />

                        )}
                    </div>

                </div>
            </main>

            {/* Sidebar */}
            <TdsSidebar
                isOpen={sidebarOpen || (!isMobile && sidebarState.isOpen)}
                onClose={handleSidebarClose}
                profileData={profileData}
                tdsData={tdsData}
                challanData={challanData}
                isProfileEditing={isProfileEditing}
                onProfileEdit={() => setIsProfileEditing(true)}
                onProfileSave={() => setIsProfileEditing(false)}
                onProfileInputChange={handleProfileInputChange}
                stateOptions={stateOptions}
                showCsiUpload={true}
                csiFile={csiFile}
                onCsiFileChange={(e) => setCsiFile(e.target.files[0])}
                isValidating={isValidating}
                onValidate={handlePrepareReturnFile}
                onCancelValidation={handleCancelValidation}
                isMobile={isMobile}
                onSidebarStateChange={handleSidebarStateChange}
                errors={[]}
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
                selectionComplete={!!(selectedFinancialYear && selectedQuarter && selectedReturnType)}
            />

            {/* CSI Confirmation Modal */}
            {
                showCsiUploadPopup && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl animate-fade-in border border-gray-100">
                            <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Upload CSI File</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                To proceed with validation, you need to upload the <strong>Challan Status Inquiry (CSI)</strong> file.
                                <br /><br />
                                Please ensure you have downloaded it from the Income Tax portal.
                            </p>
                            <div className="flex justify-end space-x-4">
                                <button
                                    onClick={() => setShowCsiUploadPopup(false)}
                                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowCsiUploadPopup(false)
                                        if (csiFileInputRef.current) {
                                            csiFileInputRef.current.value = ''
                                            csiFileInputRef.current.click()
                                        }
                                    }}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center transition-colors duration-200 shadow-md"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload CSI File
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            <input
                type="file"
                ref={csiFileInputRef}
                style={{ display: 'none' }}
                accept=".csi"
                onChange={handleCsiFileChange}
            />

            {/* CSI Download Prompt Modal */}
            {
                showDownloadPrompt && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <Upload className="w-5 h-5 mr-2 text-blue-600" />
                                Download CSI File
                            </h3>

                            <p className="text-gray-600 mb-4 text-sm">
                                Would you like to automatically download the latest CSI file from the Income Tax Portal?
                            </p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Password</label>
                                <div className="relative">
                                    <input
                                        type={showDownloadPassword ? "text" : "password"}
                                        className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        value={downloadPassword}
                                        onChange={(e) => setDownloadPassword(e.target.value)}
                                        placeholder="Enter Password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowDownloadPassword(!showDownloadPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                    >
                                        {showDownloadPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">TAN: {profileData.section1.taxDeduction}</p>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={handleCsiDownload}
                                    disabled={isDownloadingCsi}
                                    className={`flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center border border-transparent shadow-md ${isDownloadingCsi ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isDownloadingCsi ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                            Downloading...
                                        </>
                                    ) : (
                                        <span className="font-medium">Download & Continue</span>
                                    )}
                                </button>
                                <button
                                    onClick={handleSkipDownload}
                                    disabled={isDownloadingCsi}
                                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Skip (I have file)
                                </button>
                            </div>
                            <button
                                onClick={() => setShowDownloadPrompt(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Validation Lock Screen Overlay */}
            {
                isValidating && (
                    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-900 bg-opacity-80 backdrop-blur-sm cursor-wait">
                        <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-lg text-center transform transition-all scale-100">
                            <div className="relative mb-6">
                                <div className="absolute inset-0 bg-orange-100 rounded-full animate-ping opacity-75"></div>
                                <div className="relative bg-white rounded-full p-2">
                                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-600"></div>
                                </div>
                            </div>

                            <h3 className="text-2xl font-bold text-gray-800 mb-2">Validation in Progress</h3>
                            <p className="text-gray-600 mb-6">
                                Please wait while we validate your return.
                            </p>
                            <button
                                onClick={handleCancelValidation}
                                className="px-6 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors border border-red-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )
            }
            {/* Profile Pre-fill Prompt Modal */}
            {
                showPrefillPrompt && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <User className="w-5 h-5 mr-2 text-blue-600" />
                                Pre-fill Profile Data
                            </h3>

                            <p className="text-gray-600 mb-4 text-sm">
                                We will automatically extract your PAN, Email, Mobile and Address from the Income Tax Portal.
                            </p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Portal Password</label>
                                <div className="relative">
                                    <input
                                        type={showPrefillPassword ? "text" : "password"}
                                        className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        value={prefillPassword}
                                        onChange={(e) => setPrefillPassword(e.target.value)}
                                        placeholder="Enter Portal Password"
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPrefillPassword(!showPrefillPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                    >
                                        {showPrefillPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">TAN: {profileData.section1.taxDeduction}</p>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={handleProfilePrefill}
                                    disabled={isExtractingProfile || !prefillPassword}
                                    className={`flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center border border-transparent shadow-md ${(isExtractingProfile || !prefillPassword) ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isExtractingProfile ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Get Data...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Get Data
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowPrefillPrompt(false);
                                        setPrefillPassword('');
                                    }}
                                    disabled={isExtractingProfile}
                                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                            {!isExtractingProfile && (
                                <button
                                    onClick={() => setShowPrefillPrompt(false)}
                                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    </div>
                )
            }
        </div >
    )
}

export default Tds144Form
